/**
 * Dispatch and the weighbridge. MASTER_PROMPT section 8.7.
 *
 * Phase 0 finding F6: seven June dispatches read heavier on the bridge than
 * the declared weight, and every one in the same direction. Random loading
 * error scatters both ways; a one-sided gap is the instrument, not the men
 * loading the truck. This board runs that test and says so in words.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { DispatchBoard as Board } from "../../lib/types";
import { fmtDate, fmtPct, fmtQty, toNumber } from "../../lib/format";
import { BarRow, BoardPage, DailyBars, Exceptions, KpiCard, day } from "./board";
import { comparisonLabel } from "../../lib/period";
import { useCompare, useReportPeriod } from "../../lib/reportPeriod";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";

type WeighRow = Board["weighbridge"][number];

const WEIGH_COLUMNS: SheetColumn<WeighRow>[] = [
  { key: "doc_no", label: "Challan", value: (r) => r.doc_no },
  { key: "date", label: "Date", type: "date", value: (r) => r.business_date,
    format: (r) => fmtDate(r.business_date) },
  { key: "party", label: "Customer", value: (r) => r.party },
  { key: "vehicle", label: "Vehicle", value: (r) => r.vehicle_no ?? "" },
  { key: "declared", label: "Declared", type: "number", numeric: true,
    value: (r) => toNumber(r.declared_kg),
    format: (r) => fmtQty(r.declared_kg ?? 0, 0) },
  { key: "bridge", label: "Bridge", type: "number", numeric: true,
    value: (r) => toNumber(r.bridge_kg),
    format: (r) => fmtQty(r.bridge_kg ?? 0, 0) },
  // Sorts on the signed gap so the two directions separate, while the cell
  // shows the magnitude with an arrow.
  { key: "gap", label: "Gap", type: "number", numeric: true,
    value: (r) => toNumber(r.gap_kg),
    format: (r) => `${fmtQty(Math.abs(Number(r.gap_kg)), 0)} kg` },
  { key: "gap_pct", label: "Gap %", type: "number", numeric: true,
    value: (r) => (r.gap_pct === null ? null : toNumber(r.gap_pct)),
    format: (r) => (r.gap_pct === null ? "—" : fmtPct(r.gap_pct)) },
];

export function DispatchBoard() {
  // Dispatch has no shift: a lorry leaves the gate, not a shift.
  const [shared, setPeriod] = useReportPeriod();
  const period = useMemo(() => ({ ...shared, shift: undefined }), [shared]);
  const [compare] = useCompare();
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);
  const weighbridge = useSheet(board?.weighbridge ?? [], WEIGH_COLUMNS);

  const load = useCallback(() => {
    setBoard(null);
    setError(null);
    api
      .dispatchBoard(period, compare)
      .then(setBoard)
      .catch((e: ApiError) => setError(e.message));
  }, [period, compare]);

  useEffect(load, [load]);

  const partyMax = board
    ? Math.max(...board.by_party.map((p) => Number(p.pcs)), 1)
    : 1;
  const weighed = board
    ? board.bridge_heavier + board.bridge_lighter
    : 0;

  return (
    <BoardPage
      title="Dispatch and weighbridge"
      subtitle="What left the plant, and whether the bridge agrees it did"
      period={period}
      onPeriod={setPeriod}
      loading={!board}
      error={error}
    >
      {board && (
        <>
          <div className="kpirow">
            {board.kpis.map((k) => (
              <KpiCard key={k.key} kpi={k} vs={comparisonLabel(period, compare)} />
            ))}
          </div>

          <Exceptions items={board.exceptions} />

          {board.dispatches === 0 ? (
            <div className="state state--empty">
              <span className="state__glyph" aria-hidden="true">
                ⇥
              </span>
              <h3>Nothing dispatched in this period</h3>
              <p>
                Closed dispatches appear here. A dispatch still open at the
                gate is not counted as gone — it has not left.
              </p>
            </div>
          ) : (
            <>
              {/* The finding this board exists for, stated as a test result. */}
              <section className="card">
                <h3 className="section-title">Does the bridge lean?</h3>
                <p className="section-note">
                  Loading error scatters both ways. A gap that only ever falls
                  one way is the instrument.
                </p>
                {weighed === 0 ? (
                  <p className="section-note">
                    No dispatch in this period was weighed on the bridge, so there
                    is nothing to compare. That is itself the finding.
                  </p>
                ) : (
                  <div className="sided">
                    <div className="sided__row">
                      <span className="sided__label">Bridge read heavier</span>
                      <div className="sided__track">
                        <div
                          className="sided__bar"
                          style={{
                            width: `${(board.bridge_heavier / weighed) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="num mono sided__value">
                        {board.bridge_heavier}
                      </span>
                    </div>
                    <div className="sided__row">
                      <span className="sided__label">Bridge read lighter</span>
                      <div className="sided__track">
                        <div
                          className="sided__bar sided__bar--alt"
                          style={{
                            width: `${(board.bridge_lighter / weighed) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="num mono sided__value">
                        {board.bridge_lighter}
                      </span>
                    </div>
                    <p
                      className={`sided__verdict${
                        board.one_sided ? " sided__verdict--bad" : ""
                      }`}
                    >
                      {board.one_sided
                        ? `All ${weighed} differences fall the same way. Have the bridge calibrated before treating this as a loading problem.`
                        : `Differences fall both ways, which is what loading error looks like. Net ${fmtQty(board.net_gap_kg, 0)} kg across ${weighed} weighings.`}
                    </p>
                  </div>
                )}
              </section>

              <div className="board2">
                <section className="card">
                  <h3 className="section-title">By customer</h3>
                  <p className="section-note">
                    Where the output went
                  </p>
                  <div className="heat">
                    {board.by_party.map((p) => (
                      <BarRow
                        key={p.party}
                        label={p.party}
                        value={Number(p.pcs)}
                        max={partyMax}
                        display={`${fmtQty(p.pcs, 0)} pcs`}
                        sub={`${p.dispatches} trip${p.dispatches === 1 ? "" : "s"}`}
                      />
                    ))}
                  </div>
                </section>

                <section className="card">
                  <h3 className="section-title">Trips through the period</h3>
                  <p className="section-note">
                    Pieces dispatched per day — clustering at month end is a
                    despatch pattern worth knowing about
                  </p>
                  <DailyBars
                    rows={board.daily.map((d) => ({
                      date: d.business_date,
                      value: Number(d.pcs),
                      title: `${day(d.business_date)} · ${fmtQty(d.pcs, 0)} pcs across ${d.dispatches} ${
                        d.dispatches === 1 ? "dispatch" : "dispatches"
                      } · ${fmtQty(d.wt_kg, 0)} kg`,
                    }))}
                  />
                </section>
              </div>

              <section className="card">
                <h3 className="section-title">The trucks behind the gap</h3>
                <p className="section-note">
                  Largest difference first — that is the one worth walking out
                  to look at
                </p>
                {board.weighbridge.length === 0 ? (
                  <p className="section-note">
                    Every weighed dispatch matched its declared weight exactly.
                  </p>
                ) : (
                  <>
                  <SheetToolbar sheet={weighbridge} exportName="weighbridge-gaps" />
                  <div className="tablewrap">
                    <table className="table">
                      <SheetHead sheet={weighbridge} />
                      <tbody>
                        {weighbridge.rows.map((w) => (
                          <tr key={w.doc_no} className="row--warn">
                            <td className="mono">{w.doc_no}</td>
                            <td>{fmtDate(w.business_date)}</td>
                            <td>{w.party}</td>
                            <td className="mono">{w.vehicle_no ?? "—"}</td>
                            <td className="num mono">
                              {fmtQty(w.declared_kg ?? 0, 0)}
                            </td>
                            <td className="num mono">
                              {fmtQty(w.bridge_kg ?? 0, 0)}
                            </td>
                            <td className="num mono cell--negative">
                              {Number(w.gap_kg) > 0 ? "↑ " : "↓ "}
                              {fmtQty(Math.abs(Number(w.gap_kg)), 0)} kg
                            </td>
                            <td className="num mono">
                              {w.gap_pct === null ? "—" : fmtPct(w.gap_pct)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
                {board.unweighed.length > 0 && (
                  <p className="page__note">
                    {board.unweighed.length === 1
                      ? "One dispatch left without a bridge reading, so it contributes nothing to the figures above: "
                      : `${board.unweighed.length} dispatches left without a bridge reading, so they contribute nothing to the figures above: `}
                    {board.unweighed.join(", ")}. An unweighed fleet reconciles
                    perfectly, which is not the same as being right.
                  </p>
                )}
              </section>
            </>
          )}
        </>
      )}
    </BoardPage>
  );
}
